// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — Auth Context
// Manages: session, profile, impersonation (Customer Override)
// ══════════════════════════════════════════════════════════════════════════════

import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { supabase, signOut, onAuthChange } from '@lib/supabase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(null);

  // ─── Initial session load ──────────────────────────────────────────
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      if (s?.user) loadProfile(s.user.id);
      else setLoading(false);
    });

    const subscription = onAuthChange((event, s) => {
      setSession(s);
      if (event === 'SIGNED_IN' && s?.user) loadProfile(s.user.id);
      else if (event === 'SIGNED_OUT') {
        setProfile(null);
        setImpersonating(null);
        setLoading(false);
      }
    });

    return () => subscription?.unsubscribe?.();
  }, []);

  // ─── Load profile from DB, auto-create if missing (for seed users) ─
  const loadProfile = useCallback(async (userId) => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*, tenants(*)')
        .eq('id', userId)
        .maybeSingle();

      if (!data && !error) {
        // Auto-create profile for seeded users
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const newProfile = await createInitialProfile(user);
          setProfile(newProfile);
        }
      } else if (error) {
        console.error('[Auth] Profile load error:', error);
      } else {
        setProfile(data);
      }
    } catch (err) {
      console.error('[Auth] Unexpected error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const createInitialProfile = async (user) => {
    const seeds = {
      'saad@seldor.io': {
        role: 'super_admin',
        full_name: 'Saad Al-Dousari', // تم التعديل لتطابق قاعدة البيانات (بدون ar و en)
        tenant_id: 'a0000000-0000-0000-0000-000000000001' // مربوط بمقهى للعرض
      },
      'owner@brewbeyond.com': {
        role: 'customer',
        full_name: 'Ahmed Al-Farsi',
        tenant_id: 'a0000000-0000-0000-0000-000000000001'
      },
      'owner@emptycafe.com': {
        role: 'customer',
        full_name: 'Nora Khalil',
        tenant_id: 'a0000000-0000-0000-0000-000000000002'
      }
    };

    // القيمة الافتراضية لأي إيميل جديد
    const seed = seeds[user.email] || { role: 'customer', full_name: user.email, tenant_id: null };

    // استخدام upsert بأعمدة مطابقة تماماً للـ DB لتجنب الأخطاء
    const { data, error } = await supabase
      .from('profiles')
      .upsert({ id: user.id, ...seed }) 
      .select('*, tenants(*)')
      .single();

    if (error) {
      console.error('[Auth] Error creating profile:', error);
    }

    return data;
  };

  // ─── Sign out (also exits impersonation) ───────────────────────────
  const logout = useCallback(async () => {
    if (impersonating) {
      setImpersonating(null);
      return;
    }
    await signOut();
    setProfile(null);
    setSession(null);
  }, [impersonating]);

  // ─── Impersonation (Admin Override) ────────────────────────────────
  const impersonate = useCallback((customerProfile) => {
    setImpersonating(customerProfile);
  }, []);

  const exitImpersonation = useCallback(() => {
    setImpersonating(null);
  }, []);

  // ─── Effective profile (impersonated or real) ──────────────────────
  const effectiveProfile = impersonating || profile;

  const value = useMemo(() => ({
    session, profile, effectiveProfile, loading,
    impersonating, isImpersonating: !!impersonating,
    logout, impersonate, exitImpersonation,
    refreshProfile: () => session?.user && loadProfile(session.user.id),
    isSuperAdmin: profile?.role === 'super_admin'
  }), [session, profile, effectiveProfile, loading, impersonating, logout, impersonate, exitImpersonation, loadProfile]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
